import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { User, Mail, Phone, Edit3, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './AdminProfile.module.css';

const AdminProfile = () => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'admin',
    profileImage: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'
  });

  const [formData, setFormData] = useState({ ...profile });

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/profile');
      setProfile(data);
      setFormData(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch profile data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImageFile(e.target.files[0]);
      setProfileImagePreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uploadData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          uploadData.append(key, formData[key]);
        }
      });
      if (profileImageFile) {
        uploadData.append('profileImage', profileImageFile);
      }

      const { data } = await api.put('/users/profile', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const updatedProfile = { ...data };
      if (updatedProfile.profileImage && updatedProfile.profileImage.startsWith('/uploads')) {
        updatedProfile.profileImage = `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${updatedProfile.profileImage}`;
      }

      setProfile(updatedProfile);
      setFormData(updatedProfile);
      
      // Update local storage user info
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userInfo.fullName = updatedProfile.fullName;
      userInfo.phone = updatedProfile.phone;
      userInfo.profileImage = updatedProfile.profileImage;
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      setIsDrawerOpen(false);
      // Trigger a custom event to notify other components of the update
      window.dispatchEvent(new Event('profileUpdated'));
      toast.success('Admin Profile updated successfully!');
    } catch (err) {
      toast.error('Failed to update profile: ' + (err.response?.data?.message || err.message));
    }
  };

  // Helper to get correct image URL
  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Admin Profile" 
        breadcrumb={['Account', 'My Profile']} 
        actionButton={
          <button className={styles.editBtn} onClick={() => {
            setFormData(profile);
            setProfileImageFile(null);
            setProfileImagePreview(getImageUrl(profile.profileImage));
            setIsDrawerOpen(true);
          }}>
            <Edit3 size={16} /> Edit Profile
          </button>
        }
      />

      <div className={styles.profileContent}>
        {/* Left Column: Avatar & Basic */}
        <div className={styles.profileCard}>
          <div className={styles.avatarSection}>
            <img src={getImageUrl(profile.profileImage)} alt="Profile" className={styles.avatarLarge} style={{ objectFit: 'cover' }} />
            <h2>{profile.fullName}</h2>
              <span className={styles.badgeRole} style={{ background: '#eef2ff', color: '#4f46e5' }}>
              <Shield size={12} style={{ display: 'inline', marginRight: '4px' }}/>{(profile.role || 'admin').toUpperCase()}
            </span>
          </div>

          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <Mail size={18} className={styles.infoIcon} />
              <div>
                <span>Email Address</span>
                <strong>{profile.email}</strong>
              </div>
            </div>
            <div className={styles.infoItem}>
              <Phone size={18} className={styles.infoIcon} />
              <div>
                <span>Phone Number</span>
                <strong>{profile.phone || 'Not Set'}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide-over Drawer for Editing */}
      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Edit Profile"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          
          <div className={styles.uploadImageSection} onClick={() => document.getElementById('profileImgInput').click()}>
            <img src={profileImagePreview || getImageUrl(profile.profileImage)} alt="Preview" className={styles.avatarPreview} style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e7eb', cursor: 'pointer' }} />
            <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>Click to change photo</span>
            <input type="file" id="profileImgInput" style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
          </div>

          <div className="formGroup">
            <label>Full Name</label>
            <input name="fullName" value={formData.fullName} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input name="phone" value={formData.phone || ''} onChange={handleChange} />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Save Changes</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default AdminProfile;
